import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, CircularProgress, Alert, Tabs, Tab, IconButton, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Button, Menu, MenuItem, Chip, TextField, LinearProgress } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor, KeyboardSensor, TouchSensor } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { db, auth } from './firebase.ts'; // Import auth
import { collection, getDocs, addDoc, serverTimestamp, doc, updateDoc, deleteDoc, query, orderBy, writeBatch, limit, startAfter } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import AddChecklistPopup from './AddChecklistPopup.tsx';
import AddTaskPopup from './AddTaskPopup.tsx'; // Corrected import
import './ChecklistsPage.css';
import { useTheme } from './ThemeContext.tsx';
import { isBefore, isSameDay, startOfDay } from 'date-fns'; // Import date-fns functions
import { useTheme as useMuiTheme } from '@mui/material/styles'; // Import useMuiTheme
import useMediaQuery from '@mui/material/useMediaQuery'; // Import useMediaQuery
import MobileHeader from './components/MobileHeader'; // Import MobileHeader

import { User } from 'firebase/auth';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';

interface Task {
  id: string;
  name: string;
  description: string;
  videoUrl: string;
  completed: boolean;
  createdAt: any; // Firestore Timestamp
  orderIndex: number;
  isRecurring: boolean;
  isRecurringInstance: boolean;
  recurrence: { type: string; details?: any; }; // Added details to recurrence
  nextOccurrenceDates: Date[] | null; // Changed to allow null
  scheduledDate?: Date | null; // Add scheduledDate to Task interface
  checklistName?: string; // For incomplete tasks in upload confirmation
}

export interface Checklist {
  id: string;
  name: string;
  totalTasks: number;
  completedTasks: number;
  tasks: Task[];
  createdAt?: any; // Firestore Timestamp
}

interface ChecklistsPageProps {
  user: User | null;
}

function ChecklistsPage({ user }: ChecklistsPageProps) {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null); // For pagination
  const [hasMoreChecklists, setHasMoreChecklists] = useState<boolean>(true); // For pagination
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const [isAddChecklistPopupOpen, setIsAddChecklistPopupOpen] = useState<boolean>(false); // State for popup
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false); // State for delete confirmation dialog
  const [checklistToDelete, setChecklistToDelete] = useState<Checklist | null>(null); // State to store checklist to be deleted
  const [isEditMode, setIsEditMode] = useState<boolean>(false); // State for edit mode
  const [isAddTaskPopupOpen, setIsAddTaskPopupOpen] = useState<boolean>(false); // State for add task popup
  const [isUploadConfirmOpen, setIsUploadConfirmOpen] = useState<boolean>(false); // State for upload confirmation dialog
  const [incompleteTasks, setIncompleteTasks] = useState<Task[]>([]); // State to store incomplete tasks
  const [uploadScope, setUploadScope] = useState<'current' | 'all'>('current'); // 'current' or 'all'
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // For the upload dropdown menu
  const [uploaderName, setUploaderName] = useState<string>(''); // State to store uploader's name
  const [processingUpload, setProcessingUpload] = useState<boolean>(false); // State to prevent double submission
  const navigate = useNavigate(); // Initialize useNavigate
  const { theme } = useTheme(); // Use the theme hook

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  useEffect(() => {
    const fetchChecklists = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const checklistsCollectionRef = collection(db, `users/${user.uid}/checklists`);
        const initialLimit = 10; // Fetch initial 10 checklists
        const q = query(checklistsCollectionRef, orderBy('createdAt', 'desc'), limit(initialLimit));
        const checklistSnapshot = await getDocs(q);
        const newChecklistsData: Checklist[] = checklistSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name as string,
            totalTasks: data.totalTasks || 0,
            completedTasks: data.completedTasks || 0,
            tasks: [], // Initialize with empty tasks, will be loaded on demand
            createdAt: data.createdAt,
          };
        });
        setChecklists(newChecklistsData);
        if (newChecklistsData.length === 0) {
          setIsEditMode(true);
        }
        setLastVisible(checklistSnapshot.docs[checklistSnapshot.docs.length - 1]);
        setHasMoreChecklists(checklistSnapshot.docs.length === initialLimit);
      } catch (err) {
        console.error("Error fetching checklists:", err);
        setError("Failed to load checklists.");
      } finally {
        setLoading(false);
      }
    };

    const initializeChecklists = async () => {
      await fetchChecklists();
    };

    initializeChecklists();
  }, [user]);

  // Effect to fetch tasks for the selected checklist when the tab changes
  useEffect(() => {
    const fetchTasksForSelectedChecklist = async () => {
      if (!user || !checklists.length || selectedTabIndex === null) {
        return;
      }

      const currentChecklist = checklists[selectedTabIndex];
      if (!currentChecklist || currentChecklist.tasks.length > 0) {
        // Tasks already loaded or checklist not found
        return;
      }

      try {
        const tasksCollectionRef = collection(db, `users/${user.uid}/checklists/${currentChecklist.id}/tasks`);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to start of day for comparison

        const tasksSnapshot = await getDocs(query(tasksCollectionRef, orderBy('orderIndex', 'asc')));
        const tasks: Task[] = tasksSnapshot.docs
          .map(taskDoc => {
            const data = taskDoc.data();
            return {
              id: taskDoc.id,
              name: data.name as string,
              description: data.description as string,
              videoUrl: data.videoUrl as string,
              completed: data.completed || false,
              createdAt: data.createdAt,
              orderIndex: data.orderIndex || 0,
              isRecurring: data.isRecurring || false,
              isRecurringInstance: data.isRecurringInstance || false,
              recurrence: data.recurrence || { type: 'none' },
              nextOccurrenceDates: Array.isArray(data.nextOccurrenceDates)
                ? data.nextOccurrenceDates.map((ts: any) => ts.toDate())
                : null, // Allow null for nextOccurrenceDates
              scheduledDate: data.scheduledDate ? data.scheduledDate.toDate() : null,
            };
          });
        
        const filteredTasks = tasks.filter(task => {
            const todayStart = startOfDay(today); // Normalize 'today' for consistent comparison

            // Show tasks that are not recurring (one-off tasks)
            if (!task.isRecurring) {
              return true;
            }

            // If it is a recurring task
            if (task.isRecurring) {
              // If it's a recurring instance, show only if its scheduledDate is today
              if (task.isRecurringInstance && task.scheduledDate) {
                return isSameDay(task.scheduledDate, todayStart);
              }
              // If it's a recurring template (not an instance), show if any of its nextOccurrenceDates is today
              if (!task.isRecurringInstance && task.nextOccurrenceDates && task.nextOccurrenceDates.length > 0) { // Check for null
                return task.nextOccurrenceDates.some(occurrenceDate =>
                  isSameDay(occurrenceDate, todayStart)
                );
              }
            }
            // Hide all other tasks (e.g., recurring tasks not scheduled for today, or invalid tasks)
            return false;
          });

        setChecklists(prevChecklists =>
          prevChecklists.map((checklist, index) =>
            index === selectedTabIndex
              ? { ...checklist, tasks: filteredTasks as Task[] }
              : checklist
          )
        );
      } catch (err) {
        console.error("Error fetching tasks for selected checklist:", err);
        setError("Failed to load tasks for the selected checklist.");
      }
    };

    fetchTasksForSelectedChecklist();
  }, [user, checklists, selectedTabIndex]); // Depend on user, checklists, and selectedTabIndex

  const handleLoadMoreChecklists = async () => {
    if (!user || !hasMoreChecklists || !lastVisible) {
      return;
    }
    setLoading(true);
    try {
      const checklistsCollectionRef = collection(db, `users/${user.uid}/checklists`);
      const nextLimit = 10;
      const q = query(checklistsCollectionRef, orderBy('createdAt', 'desc'), startAfter(lastVisible), limit(nextLimit));
      const checklistSnapshot = await getDocs(q);
      const newChecklistsData: Checklist[] = checklistSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name as string,
          totalTasks: data.totalTasks || 0,
          completedTasks: data.completedTasks || 0,
          tasks: [],
          createdAt: data.createdAt,
        };
      });

      setChecklists(prevChecklists => [...prevChecklists, ...newChecklistsData]);
      setLastVisible(checklistSnapshot.docs[checklistSnapshot.docs.length - 1]);
      setHasMoreChecklists(checklistSnapshot.docs.length === nextLimit);
    } catch (err) {
      console.error("Error loading more checklists:", err);
      setError("Failed to load more checklists.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };

  const handleTaskCompletionChange = async (checklistId: string, taskId: string, isCompleted: boolean) => {
    if (!user) return;
    try {
      const taskRef = doc(db, `users/${user.uid}/checklists/${checklistId}/tasks`, taskId);
      await updateDoc(taskRef, {
        completed: isCompleted,
      });

      // Update completedTasks count on the parent checklist document
      const checklistRef = doc(db, `users/${user.uid}/checklists`, checklistId);
      await updateDoc(checklistRef, {
        completedTasks: isCompleted ? (selectedChecklist?.completedTasks || 0) + 1 : (selectedChecklist?.completedTasks || 0) - 1,
      });

      // Update local state to reflect the change
      setChecklists(prevChecklists =>
        prevChecklists.map(checklist =>
          checklist.id === checklistId
            ? {
                ...checklist,
                tasks: checklist.tasks.map(task =>
                  task.id === taskId ? { ...task, completed: isCompleted } : task
                ),
                completedTasks: isCompleted ? (checklist.completedTasks || 0) + 1 : (checklist.completedTasks || 0) - 1,
              }
            : checklist
        )
      );
    } catch (error) {
      console.error("Error updating task completion:", error);
      setUploadMessage("Error updating task completion.");
    }
  };

  const selectedChecklist = checklists[selectedTabIndex];

  const handleUploadChecklist = async (checklistToUpload: Checklist, uploadedBy: string = '') => { // Added uploadedBy parameter
    if (!user) {
      setUploadMessage("User not authenticated. Cannot upload checklist.");
      return false;
    }
    if (processingUpload) { // Prevent multiple submissions
      return false;
    }
    setProcessingUpload(true); // Set processing to true

    try {
      const reportData: {
        checklistName: string;
        dateCompleted: string;
        tasks: { description: string; completed: boolean; }[];
        timestamp: any;
        userId: string;
        uploadedBy?: string;
      } = {
        checklistName: checklistToUpload.name,
        dateCompleted: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
        tasks: checklistToUpload.tasks.map(task => ({
          description: task.name,
          completed: task.completed,
        })),
        timestamp: serverTimestamp(),
        userId: user.uid,
      };

      if (uploadedBy) {
        reportData.uploadedBy = uploadedBy; // Add uploader's name if provided
      }

      const newChecklistReportRef = await addDoc(collection(db, 'completedChecklistReports'), reportData);

      // Create notification for the uploaded checklist
      const notificationUserName = uploadedBy || user.displayName || user.email || "A user";
      const notificationRef = collection(db, `users/${user.uid}/notifications`);
      await addDoc(notificationRef, {
        title: "Checklist Completed!",
        message: `${notificationUserName} has completed the checklist: ${checklistToUpload.name}.`,
        timestamp: serverTimestamp(),
        read: false,
        checklistId: newChecklistReportRef.id,
      });
      console.log(`Notification created for user ${user.uid} for checklist ${newChecklistReportRef.id}`);

      // Reset all tasks in the current checklist to incomplete
      for (const task of checklistToUpload.tasks) {
        const taskRef = doc(db, `users/${user.uid}/checklists/${checklistToUpload.id}/tasks`, task.id);
        await updateDoc(taskRef, {
          completed: false,
        });
      }

      // Update local state to reflect the reset
      setChecklists(prevChecklists =>
        prevChecklists.map(checklist =>
          checklist.id === checklistToUpload.id
            ? {
                ...checklist,
                tasks: checklist.tasks.map(task => ({ ...task, completed: false })),
              }
            : checklist
        )
      );
      return true;
    } catch (error) {
      console.error('Error uploading checklist report or resetting checklist: ', error);
      setUploadMessage(`Error uploading report for "${checklistToUpload.name}". Please try again.`);
      return false;
    } finally {
      setProcessingUpload(false); // Reset processing state
    }
  };

  const handleUploadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUploadOptionSelect = async (option: 'current' | 'all') => {
    setUploadScope(option);
    setAnchorEl(null); // Close the menu

    if (option === 'current') {
      if (!selectedChecklist) {
        setUploadMessage("No checklist selected to upload.");
        return;
      }
      const uncompleted = selectedChecklist.tasks.filter(task => !task.completed);
      setIncompleteTasks(uncompleted); // Always set incomplete tasks
      setIsUploadConfirmOpen(true); // Always open the consolidated dialog
    } else if (option === 'all') {
      const allUncompletedTasks = checklists.flatMap(checklist =>
        checklist.tasks.filter(task => !task.completed).map(task => ({ ...task, checklistName: checklist.name }))
      );

      if (allUncompletedTasks.length > 0) {
        setIncompleteTasks(allUncompletedTasks);
        setIsUploadConfirmOpen(true);
      } else {
        setUploadMessage('');
        let allSuccess = true;
        for (const checklist of checklists) {
          const success = await handleUploadChecklist(checklist);
          if (!success) allSuccess = false;
        }
        if (allSuccess) {
          setUploadMessage('All checklist reports uploaded successfully and checklists reset!');
        } else {
          setUploadMessage('Some checklists failed to upload. Please check console for details.');
        }
      }
    }
  };

  const handleConfirmUpload = async () => {
    console.log('handleConfirmUpload triggered. uploadScope:', uploadScope, 'uploaderName:', uploaderName.trim());
    setIsUploadConfirmOpen(false);
    setUploadMessage('');
    if (uploadScope === 'current') {
      console.log('Calling handleUploadChecklist for current checklist with uploaderName:', uploaderName.trim());
      const success = await handleUploadChecklist(selectedChecklist, uploaderName.trim()); // Pass uploaderName
      if (success) {
        setUploadMessage('Checklist report uploaded successfully and checklist reset!');
        setUploaderName(''); // Clear uploader name after successful upload
      }
    } else if (uploadScope === 'all') {
      console.log('Calling handleUploadChecklist for all checklists with uploaderName:', uploaderName.trim());
      let allSuccess = true;
      for (const checklist of checklists) {
        const success = await handleUploadChecklist(checklist, uploaderName.trim()); // Pass uploaderName here too
        if (!success) allSuccess = false;
      }
      if (allSuccess) {
        setUploadMessage('All checklist reports uploaded successfully and checklists reset!');
      } else {
        setUploadMessage('Some checklists failed to upload. Please check console for details.');
      }
    }
  };

  const handleAddChecklist = async (checklistName: string) => {
    if (!user) {
      setUploadMessage("User not authenticated. Cannot add checklist.");
      return;
    }
    try {
      const checklistsCollectionRef = collection(db, `users/${user.uid}/checklists`);
      const newChecklistRef = await addDoc(checklistsCollectionRef, {
        name: checklistName,
        createdAt: serverTimestamp(),
        totalTasks: 0, // Initialize denormalized count
        completedTasks: 0, // Initialize denormalized count
      });

      // Add the new checklist to the local state
      setChecklists(prevChecklists => [
        { id: newChecklistRef.id, name: checklistName, totalTasks: 0, completedTasks: 0, tasks: [] }, // Add denormalized counts
        ...prevChecklists,
      ]);
      setSelectedTabIndex(0); // Select the new checklist
      setIsAddChecklistPopupOpen(false); // Close the popup
      setUploadMessage(`Checklist "${checklistName}" added successfully!`);
    } catch (error) {
      console.error("Error adding new checklist:", error);
      setUploadMessage("Error adding new checklist. Please try again.");
    }
  };

  const handleAddTask = async ({ name, description, videoUrl, checklistId, isRecurring, recurrence, nextOccurrenceDates }: { name: string; description: string; videoUrl: string; checklistId: string; isRecurring: boolean; recurrence: { type: string; details: any; }; nextOccurrenceDates: Date[] | null; }) => {
    if (!user || !checklistId) {
      setUploadMessage("User not authenticated or no checklist selected. Cannot add task.");
      return;
    }
    try {
      const tasksCollectionRef = collection(db, `users/${user.uid}/checklists/${checklistId}/tasks`);
      const tasksSnapshot = await getDocs(tasksCollectionRef);
      const newOrderIndex = tasksSnapshot.size > 0 ? Math.max(...tasksSnapshot.docs.map(doc => doc.data().orderIndex || 0)) + 1 : 0;

      const taskData: Omit<Task, 'id'> = {
        name: name,
        description: description,
        videoUrl: videoUrl,
        completed: false,
        createdAt: serverTimestamp(),
        orderIndex: newOrderIndex,
        isRecurring: isRecurring,
        isRecurringInstance: false,
        recurrence: { type: 'none' }, // Initialize with default
        nextOccurrenceDates: [], // Initialize with default
      };

      if (isRecurring) {
        taskData.recurrence = recurrence;
        taskData.nextOccurrenceDates = nextOccurrenceDates;
        (taskData as any).originalChecklistId = checklistId; // Add originalChecklistId for recurring templates
      } else {
        taskData.recurrence = { type: 'none' };
      }

      const newTaskRef = await addDoc(tasksCollectionRef, taskData);

      // Update totalTasks count on the parent checklist document
      const checklistRef = doc(db, `users/${user.uid}/checklists`, checklistId);
      await updateDoc(checklistRef, {
        totalTasks: (selectedChecklist?.totalTasks || 0) + 1,
      });

      // Update local state to include the newly created task and updated checklist counts
      setChecklists(prevChecklists =>
        prevChecklists.map(checklist =>
          checklist.id === checklistId
            ? {
                ...checklist,
                tasks: [...checklist.tasks, { id: newTaskRef.id, ...taskData }],
                totalTasks: (checklist.totalTasks || 0) + 1,
              }
            : checklist
        )
      );
      setIsAddTaskPopupOpen(false); // Close the popup
      setUploadMessage(`Task "${name}" added successfully to "${selectedChecklist?.name}"!`);
    } catch (error) {
      console.error("Error adding new task:", error);
      setUploadMessage("Error adding new task. Please try again.");
    }
  };

  const handleDeleteChecklist = async (checklistId: string | undefined) => {
    if (!user || !checklistId) {
      setUploadMessage("User not authenticated or checklist ID is missing. Cannot delete checklist.");
      return;
    }
    try {
      // Delete all tasks within the checklist subcollection first
      const tasksCollectionRef = collection(db, `users/${user.uid}/checklists/${checklistId}/tasks`);
      const tasksSnapshot = await getDocs(tasksCollectionRef);
      const deletedTasksCount = tasksSnapshot.docs.length;
      const deletedCompletedTasksCount = tasksSnapshot.docs.filter(taskDoc => taskDoc.data().completed).length;

      const deleteTasksPromises = tasksSnapshot.docs.map(taskDoc => deleteDoc(doc(db, `users/${user.uid}/checklists/${checklistId}/tasks`, taskDoc.id)));
      await Promise.all(deleteTasksPromises);

      // Then delete the checklist document itself
      await deleteDoc(doc(db, `users/${user.uid}/checklists`, checklistId));

      // Update totalTasks and completedTasks on the parent user document (if applicable, or just remove the checklist)
      // For now, we just remove the checklist from local state, as the checklist document itself is deleted.
      // If you had a higher-level aggregate, you'd update it here.

      // Update local state to remove the deleted checklist
      setChecklists(prevChecklists => prevChecklists.filter(checklist => checklist.id !== checklistId));

      // Adjust selected tab if the deleted tab was currently selected
      if (selectedChecklist && selectedChecklist.id === checklistId) {
        setSelectedTabIndex(0); // Go to the first tab or a default
      }
      setUploadMessage("Checklist deleted successfully!");
    } catch (error) {
      console.error("Error deleting checklist:", error);
      setUploadMessage("Error deleting checklist. Please try again.");
    } finally {
      setIsDeleteDialogOpen(false); // Close the confirmation dialog
      setChecklistToDelete(null); // Clear the checklist to delete
    }
  };

  const confirmDeleteChecklist = (checklist: Checklist) => {
    setChecklistToDelete(checklist);
    setIsDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ textAlign: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  const handleDragEnd = async (event: any) => { // event type from @dnd-kit/core
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    if (!selectedChecklist || !user) {
      return;
    }

    const checklistId = selectedChecklist.id;

    const oldIndex = selectedChecklist.tasks.findIndex(task => task.id === active.id);
    const newIndex = selectedChecklist.tasks.findIndex(task => task.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newTasks: Task[] = arrayMove(selectedChecklist.tasks, oldIndex, newIndex);

    // Update orderIndex for affected tasks in Firestore
    const batch = writeBatch(db);
    newTasks.forEach((task, index) => {
      const taskRef = doc(db, `users/${user.uid}/checklists/${checklistId}/tasks`, task.id);
      batch.update(taskRef, { orderIndex: index });
    });

    try {
      await batch.commit();
      console.log("Task order updated in Firestore.");
      setChecklists(prevChecklists =>
        prevChecklists.map(checklist =>
          checklist.id === checklistId
            ? { ...checklist, tasks: newTasks }
            : checklist
        )
      );
    } catch (error) {
      console.error("Error updating task order in Firestore:", error);
      setUploadMessage("Error updating task order. Please try again.");
    }
  };

  // Component for individual sortable task items
  interface SortableTaskItemProps {
    task: Task;
    checklistId: string;
    handleTaskCompletionChange: (checklistId: string, taskId: string, isCompleted: boolean) => Promise<void>;
    navigate: ReturnType<typeof useNavigate>;
    isEditMode: boolean;
    theme: any; // Define a proper type for theme if available
  }

  function SortableTaskItem({ task, checklistId, handleTaskCompletionChange, navigate, isEditMode, theme }: SortableTaskItemProps) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({ id: task.id, disabled: !isEditMode });

    const style = {
      transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
      transition,
      cursor: isEditMode ? 'grab' : 'default',
      touchAction: 'none', // Add this line
    };

    return (
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="checklist-item"
        sx={{
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: theme.colors.taskCardBackground,
          borderRadius: '12px',
          padding: '10px 15px',
          boxShadow: theme.boxShadow,
        }}
      >
        <Checkbox
          checked={task.completed}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTaskCompletionChange(checklistId, task.id, e.target.checked)}
          sx={{
            color: theme.colors.lightText,
            '&.Mui-checked': {
              color: theme.colors.primaryRed,
            },
          }}
        />
        <Typography
          variant="body1"
          sx={{
            cursor: 'pointer',
            fontWeight: '500',
            flexGrow: 1,
            textDecoration: task.completed ? 'line-through' : 'none',
            color: task.completed ? theme.colors.lightText : theme.colors.darkText,
          }}
          onClick={() => navigate(`/checklists/${checklistId}/tasks/${task.id}`)}
        >
          {task.name}
          {task.recurrence?.type && typeof task.recurrence.type === 'string' && task.recurrence.type !== 'none' && (
            <Chip
              label={task.recurrence.type.charAt(0).toUpperCase() + task.recurrence.type.slice(1)}
              size="small"
              sx={{
                ml: 1,
                backgroundColor: theme.colors.primaryRed,
                color: theme.colors.whiteCardBackground,
                fontWeight: 'bold',
                fontSize: '0.7em',
                height: '20px',
              }}
            />
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Container sx={{ pt: 0, px: 0 }} maxWidth={false}>
      {isMobile && <MobileHeader title="Checklists" />}

      <Box sx={{ mt: isMobile ? '70px' : '30px', px: 2, pb: 2, backgroundColor: theme.colors.lightGrayBackground }}> {/* Adjusted mt */}
        {uploadMessage && (
          <Alert severity={uploadMessage.includes('successfully') ? 'success' : 'error'} sx={{ mb: 2 }}>
            {uploadMessage}
          </Alert>
        )}

      <Tabs
        value={selectedTabIndex}
        onChange={handleTabChange}
        aria-label="checklist tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          backgroundColor: theme.colors.lightGrayBackground, /* Set tabs background to light gray */
          '& .MuiTabs-indicator': {
            backgroundColor: theme.colors.primaryRed,
            height: '3px',
            borderRadius: '3px 3px 0 0',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: '600',
            color: theme.colors.lightText,
            padding: '12px 20px',
            minHeight: 'unset',
            '&.Mui-selected': {
              color: theme.colors.primaryRed,
            },
          },
          borderBottom: '1px solid var(--theme-colors-borderColor)',
          mb: 3,
        }}
      >
        {checklists.map((checklist, index) => (
          <Tab
            key={checklist.id}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {checklist.name}
                {isEditMode && (
                  <Box
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDeleteChecklist(checklist);
                    }}
                    sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: theme.colors.lightText }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Box>
                )}
              </Box>
            }
          />
        ))}
        {isEditMode && (
          <Tab
            icon={<AddIcon />}
            aria-label="add new checklist"
            onClick={() => setIsAddChecklistPopupOpen(true)}
          />
        )}
      </Tabs>

      {hasMoreChecklists && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={handleLoadMoreChecklists} disabled={loading} sx={{ color: theme.colors.lightText }}>
            Load More Checklists
          </Button>
        </Box>
      )}

      {checklists.length === 0 && !isEditMode && (
        <Typography variant="body1" align="center" sx={{ mt: 2, color: theme.colors.lightText }}>
          No checklists found.
        </Typography>
      )}
      {checklists.length === 0 && isEditMode && (
        <Typography variant="body1" align="center" sx={{ mt: 2, color: theme.colors.lightText }}>
          No checklists found. Click the '+' tab to add a new one.
        </Typography>
      )}

      {selectedChecklist && (
        <Box
          sx={{
            backgroundColor: theme.colors.lightGrayBackground, /* Set checklist details background to light gray */
            borderRadius: '12px',
            padding: '20px',
            boxShadow: 'none', /* Removed shadow as requested */
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ color: theme.colors.darkText, fontWeight: 'bold' }}>
              {selectedChecklist.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IconButton
                color="primary"
                aria-label="toggle edit mode"
                onClick={() => setIsEditMode(!isEditMode)}
                sx={{ color: isEditMode ? theme.colors.primaryRed : theme.colors.lightText }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="primary"
                aria-label="upload checklist"
                onClick={handleUploadClick}
                sx={{ color: theme.colors.blueUpload }}
              >
                <CloudUploadIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => handleUploadOptionSelect('current')}>Upload only this checklist</MenuItem>
                <MenuItem onClick={() => handleUploadOptionSelect('all')}>Upload all checklists</MenuItem>
              </Menu>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: theme.colors.lightText, mb: 2 }}>
            {selectedChecklist.tasks.filter(task => task.completed).length} of {selectedChecklist.tasks.length} tasks completed
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(selectedChecklist.tasks.filter(task => task.completed).length / selectedChecklist.tasks.length) * 100 || 0}
            sx={{
              height: 8,
              borderRadius: 5,
              backgroundColor: theme.colors.lightGrayBackground,
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.15)', /* Made inward shadow more visible */
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.colors.primaryRed,
              },
              mb: 2,
            }}
          />
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedChecklist.tasks.map(task => task.id)}
              strategy={verticalListSortingStrategy}
            >
              <Box className="tasks-list" sx={{ mt: 2 }}>
                {selectedChecklist.tasks.map((task) => (
                  <SortableTaskItem
                    key={task.id}
                    task={task}
                    checklistId={selectedChecklist.id}
                    handleTaskCompletionChange={handleTaskCompletionChange}
                    navigate={navigate}
                    isEditMode={isEditMode}
                    theme={theme}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
          {isEditMode && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                if (selectedChecklist) {
                  setIsAddTaskPopupOpen(true);
                } else {
                  setUploadMessage("Please select a checklist before adding a task.");
                }
              }}
              sx={{
                mt: 2,
                width: '100%',
                textTransform: 'none',
                fontWeight: 'bold',
                backgroundColor: theme.colors.primaryRed,
                color: theme.colors.whiteCardBackground,
                borderRadius: '12px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease-in-out', // Add transition for smooth effect
                '&:hover': {
                  backgroundColor: theme.colors.primaryRed, // Slightly darker on hover
                  color: theme.colors.whiteCardBackground,
                  transform: 'translateY(-3px)', // Move up slightly
                  boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)', // More pronounced shadow
                },
              }}
            >
              Add new task
            </Button>
          )}
        </Box>
      )}

      <AddChecklistPopup
        open={isAddChecklistPopupOpen}
        onClose={() => setIsAddChecklistPopupOpen(false)}
        onAdd={handleAddChecklist}
      />

      <AddTaskPopup
        isOpen={isAddTaskPopupOpen}
        onClose={() => setIsAddTaskPopupOpen(false)}
        onAddTask={handleAddTask}
        selectedChecklist={selectedChecklist}
        checklists={checklists} // Pass all checklists
      />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ color: theme.colors.darkText, fontWeight: 'bold' }}>{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <Typography id="alert-dialog-description" sx={{ color: theme.colors.lightText }}>
            Are you sure you want to delete the checklist "{checklistToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} sx={{ color: theme.colors.lightText }}>Cancel</Button>
          <Button onClick={() => handleDeleteChecklist(checklistToDelete?.id)} autoFocus variant="contained" sx={{ backgroundColor: `${theme.colors.primaryRed} !important`, color: `${theme.colors.whiteCardBackground} !important` }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isUploadConfirmOpen}
        onClose={() => setIsUploadConfirmOpen(false)}
        aria-labelledby="upload-confirm-dialog-title"
        aria-describedby="upload-confirm-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.taskCardBackground, // Set dialog background
            color: theme.colors.darkText,
            borderRadius: theme.borderRadius,
            boxShadow: theme.boxShadow,
            border: `1px solid ${theme.colors.borderColor}`,
          }
        }}
      >
        <DialogTitle id="upload-confirm-dialog-title" sx={{ color: theme.colors.darkText, fontWeight: 'bold' }}>
          {incompleteTasks.length > 0 ? "Uncompleted Tasks" : "Upload Checklist"}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.colors.taskCardBackground }}> {/* Ensure content background matches */}
          <Typography id="upload-confirm-dialog-description" sx={{ mb: 2, color: theme.colors.lightText }}>
            {incompleteTasks.length > 0 ? "Not all tasks are completed yet. Are you sure you want to upload the checklist(s)?" : "All tasks are completed! Please enter your name to upload the checklist."}
          </Typography>
          {incompleteTasks.length > 0 && (
            <ul sx={{ color: theme.colors.lightText }}> {/* Apply text color to list items */}
              {incompleteTasks.map((task, index) => (
                <li key={index}>
                  {task.checklistName ? `[${task.checklistName}] ` : ''}{task.name}
                </li>
              ))}
            </ul>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={incompleteTasks.length > 0 ? "Your Name (Optional)" : "Your Name"}
            type="text"
            fullWidth
            variant="outlined"
            value={uploaderName}
            onChange={(e) => setUploaderName(e.target.value)}
            required={incompleteTasks.length === 0} // Make required if all tasks are completed
            InputLabelProps={{
              sx: { color: theme.colors.lightText }
            }}
            InputProps={{
              sx: { color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: theme.colors.taskCardBackground }}> {/* Ensure actions background matches */}
          <Button onClick={() => { setIsUploadConfirmOpen(false); setUploaderName(''); }} sx={{ color: theme.colors.lightText }}>Cancel</Button>
          <Button
            onClick={handleConfirmUpload}
            disabled={processingUpload || (incompleteTasks.length === 0 && !uploaderName.trim())} // Disable if no name for completed tasks
            autoFocus
            variant="contained"
            sx={{ backgroundColor: `${theme.colors.primaryRed} !important`, color: `${theme.colors.whiteCardBackground} !important` }}
          >
            {incompleteTasks.length > 0 ? "Upload Anyway" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
}

export default ChecklistsPage;
